import React, { useState, useEffect } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import StatCard from './components/StatCard';
import ReferralCard from './components/ReferralCard';

import CommissionBreakdown from './components/CommissionBreakdown';
import FilterControls from './components/FilterControls';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import commissionService from '../../services/commissionService';
import { supabase } from '../../lib/supabase';

const AffiliateDashboard = () => {
  const { userProfile } = useAuth();
  const [activeTab, setActiveTab] = useState('jobs');
  const [filters, setFilters] = useState({
    dateRange: 'month',
    referralType: 'all',
    status: 'all',
    sortBy: 'date-desc'
  });
  const [affiliateData, setAffiliateData] = useState(null);
  const [commissions, setCommissions] = useState([]);
  const [stats, setStats] = useState({
    totalReferrals: 0,
    totalRevenue: 0,
    totalCommission: 0,
    activeReferrals: 0
  });
  const [commissionBreakdown, setCommissionBreakdown] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (userProfile?.id) {
      loadAffiliateData();
    }
  }, [userProfile]);

  useEffect(() => {
    if (affiliateData?.id) {
      const channel = supabase?.channel('affiliate-commissions')?.on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'commissions',
            filter: `affiliate_id=eq.${affiliateData?.id}`
          },
          (payload) => {
            if (payload?.eventType === 'INSERT') {
              setCommissions(prev => [payload?.new, ...prev]);
              updateStats(payload?.new, 'add');
            } else if (payload?.eventType === 'UPDATE') {
              setCommissions(prev => prev?.map(c => 
                c?.id === payload?.new?.id ? payload?.new : c
              ));
            }
          }
        )?.subscribe();

      return () => supabase?.removeChannel(channel);
    }
  }, [affiliateData]);

  const loadAffiliateData = async () => {
    try {
      setLoading(true);
      const affiliate = await commissionService?.getAffiliateByUserId(userProfile?.id);
      setAffiliateData(affiliate);

      setStats({
        totalReferrals: affiliate?.total_referrals || 0,
        totalRevenue: parseFloat(affiliate?.total_revenue || 0),
        totalCommission: parseFloat(affiliate?.total_commission || 0),
        activeReferrals: affiliate?.total_referrals || 0
      });

      const commissionsData = await commissionService?.getAffiliateCommissions(affiliate?.id, filters);
      setCommissions(commissionsData);

      const breakdown = await commissionService?.getCommissionBreakdown(affiliate?.id);
      setCommissionBreakdown([
        {
          label: "Opdrachten Commissie",
          count: breakdown?.jobReferrals?.count,
          amount: breakdown?.jobReferrals?.amount,
          percentage: breakdown?.jobReferrals?.amount > 0 
            ? Math.round((breakdown?.jobReferrals?.amount / (breakdown?.jobReferrals?.amount + breakdown?.contractorReferrals?.amount)) * 100)
            : 0,
          icon: "Briefcase",
          color: "bg-primary/10 text-primary"
        },
        {
          label: "Contractor Commissie",
          count: breakdown?.contractorReferrals?.count,
          amount: breakdown?.contractorReferrals?.amount,
          percentage: breakdown?.contractorReferrals?.amount > 0
            ? Math.round((breakdown?.contractorReferrals?.amount / (breakdown?.jobReferrals?.amount + breakdown?.contractorReferrals?.amount)) * 100)
            : 0,
          icon: "Users",
          color: "bg-accent/10 text-accent"
        }
      ]);
    } catch (err) {
      console.error('Error loading affiliate data:', err);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const updateStats = (newCommission, action) => {
    if (action === 'add') {
      setStats(prev => ({
        ...prev,
        totalCommission: prev?.totalCommission + parseFloat(newCommission?.commission_amount),
        totalRevenue: prev?.totalRevenue + parseFloat(newCommission?.revenue_amount),
        totalReferrals: prev?.totalReferrals + 1
      }));
    }
  };

  const jobReferrals = commissions?.filter(c => c?.referral_type === 'Job Referral');
  const contractorReferrals = commissions?.filter(c => c?.referral_type === 'Contractor Referral');

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Laden...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <Icon name="AlertCircle" size={48} className="text-error mx-auto mb-4" />
            <p className="text-error">{error}</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6 md:space-y-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground mb-2">
              Affiliate Dashboard
            </h1>
            <p className="text-muted-foreground">
              Volg uw verwijzingen en commissies in real-time
            </p>
          </div>
          <Button iconName="Download" iconPosition="left">
            Rapport Downloaden
          </Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          <StatCard
            label="Totaal Verwijzingen"
            title="Totaal Verwijzingen"
            value={stats?.totalReferrals}
            icon="Users"
            trend="up"
            trendValue="+12% deze maand"
            color="primary"
          />
          <StatCard
            label="Totale Omzet"
            title="Totale Omzet"
            value={`€${stats?.totalRevenue?.toLocaleString('nl-NL')}`}
            icon="TrendingUp"
            trend="up"
            trendValue="+8% deze maand"
            color="success"
          />
          <StatCard
            label="Totale Commissie"
            title="Totale Commissie"
            value={`€${stats?.totalCommission?.toLocaleString('nl-NL')}`}
            icon="DollarSign"
            trend="up"
            trendValue="+15% deze maand"
            color="accent"
          />
          <StatCard
            label="Actieve Verwijzingen"
            title="Actieve Verwijzingen"
            value={stats?.activeReferrals}
            icon="Activity"
            trend="neutral"
            trendValue="Stabiel"
            color="secondary"
          />
        </div>

        <FilterControls filters={filters} onFilterChange={setFilters} onExport={() => {}} />

        <div className="bg-card border border-border rounded-lg p-4 md:p-6 elevation-sm">
          <div className="flex items-center gap-4 mb-6 border-b border-border">
            <button
              onClick={() => setActiveTab('jobs')}
              className={`pb-3 px-4 font-medium transition-smooth ${
                activeTab === 'jobs' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Opdracht Verwijzingen ({jobReferrals?.length})
            </button>
            <button
              onClick={() => setActiveTab('contractors')}
              className={`pb-3 px-4 font-medium transition-smooth ${
                activeTab === 'contractors' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Contractor Verwijzingen ({contractorReferrals?.length})
            </button>
          </div>

          <div className="space-y-4">
            {activeTab === 'jobs' && jobReferrals?.map((commission) => (
              <ReferralCard
                key={commission?.id}
                referral={{
                  id: commission?.id,
                  title: commission?.job?.title || 'Job',
                  location: commission?.job?.location || 'Unknown',
                  status: commission?.status,
                  revenue: parseFloat(commission?.revenue_amount),
                  commission: parseFloat(commission?.commission_amount),
                  commissionRate: parseFloat(commission?.commission_rate),
                  referredDate: commission?.created_at
                }}
                type="job"
              />
            ))}
            {activeTab === 'contractors' && contractorReferrals?.map((commission) => (
              <ReferralCard
                key={commission?.id}
                referral={{
                  id: commission?.id,
                  name: commission?.contractor?.full_name || 'Contractor',
                  location: 'Netherlands',
                  status: commission?.status,
                  revenue: parseFloat(commission?.revenue_amount),
                  commission: parseFloat(commission?.commission_amount),
                  commissionRate: parseFloat(commission?.commission_rate),
                  referredDate: commission?.created_at
                }}
                type="contractor"
              />
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <CommissionBreakdown breakdown={commissionBreakdown} />
        </div>
      </div>
    </MainLayout>
  );
};

export default AffiliateDashboard;